/**
 * Clock-It Email Confirmation Component
 * Matches the original Clock-It styling from SecureLogin component
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';

// UI Components - Clock-It Design System
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';

import { 
  Mail, 
  Shield, 
  AlertTriangle, 
  CheckCircle, 
  Loader, 
  RefreshCw,
  Clock
} from 'lucide-react';

interface EmailConfirmationProps {
  email: string;
  onBack: () => void;
  onConfirmed: () => void;
  onContinueToApp: () => void; // Allow proceeding to onboarding without verification
  isNewUser?: boolean;
}

export function EmailConfirmation({ email, onBack, onConfirmed, onContinueToApp, isNewUser = true }: EmailConfirmationProps) {
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { resendConfirmation } = useAuth();

  // Check if we're in development mode
  const isDev = import.meta.env.DEV || import.meta.env.VITE_ENV === 'local';

  // Auto-check for confirmation after OAuth redirect
  useEffect(() => {
    const checkForConfirmation = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          console.log('✅ Email confirmed via callback');
          onConfirmed();
        }
      } catch (error) {
        console.error('❌ Error checking confirmation:', error);
      }
    };

    // Check immediately and then every 3 seconds
    checkForConfirmation();
    const interval = setInterval(checkForConfirmation, 3000);

    return () => clearInterval(interval);
  }, [onConfirmed]);

  const handleResendConfirmation = async () => {
    setIsResending(true);
    setError('');
    setSuccess('');

    try {
      const result = await resendConfirmation(email);
      if (result.success) {
        setSuccess('Confirmation email sent! Check your inbox and spam folder.');
      } else {
        throw new Error(result.error || 'Failed to resend confirmation');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        {/* App Logo and Title - Clock-It Style */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center">
            <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center shadow-lg">
              <Clock className="w-12 h-12 text-white" />
            </div>
          </div>
          <h1 className="text-2xl font-bold">Clock-It</h1>
          <p className="text-muted-foreground">
            Worker-first time tracking & rights protection
          </p>
        </div>

        <motion.div 
          className="w-full"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          {/* Email Confirmation Card - Clock-It Style */}
          <Card className="clock-it-card">
            <CardHeader className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-blue-600" />
              </div>
              <CardTitle className="text-2xl mb-2">📧 We sent you a verification link!</CardTitle>
              <p className="text-sm text-muted-foreground">
                Check your email at:
              </p>
              <p className="text-blue-600 font-semibold mt-2">{email}</p>
              <p className="text-sm text-muted-foreground mt-2">
                We'll automatically detect when you're verified
              </p>
            </CardHeader>

            <CardContent className="space-y-6">
              {/* Worker-friendly Instructions */}
              <Alert>
                <Shield className="w-4 h-4" />
                <AlertDescription>
                  <strong>📱 Super easy:</strong> Just click the link in your email and you're done! 
                  We'll detect it automatically and celebrate with you 🎉
                </AlertDescription>
              </Alert>

              {/* Auto-polling indicator */}
              <div className="text-center text-sm text-muted-foreground">
                <div className="flex items-center justify-center space-x-2">
                  <Loader className="w-4 h-4 animate-spin" />
                  <span>⏳ Checking for verification...</span>
                </div>
              </div>

              {/* Development Notice */}
              {isDev && (
                <Alert>
                  <AlertTriangle className="w-4 h-4" />
                  <AlertDescription>
                    <strong>Development Mode:</strong> If emails aren't working, check your Supabase 
                    authentication settings. Email confirmation may need to be disabled for development.
                  </AlertDescription>
                </Alert>
              )}

              {/* Action Buttons */}
              <div className="space-y-3">
                {/* Primary CTA - Continue to App */}
                <Button
                  onClick={onContinueToApp}
                  className="w-full h-12 bg-primary hover:bg-primary/90"
                >
                  Continue to App →
                </Button>

                {/* Secondary Actions */}
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={handleResendConfirmation}
                    disabled={isResending}
                    variant="outline"
                    className="h-12"
                  >
                    {isResending ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin mr-1" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Resend Email
                      </>
                    )}
                  </Button>

                  <Button
                    variant="outline"
                    onClick={onBack}
                    className="h-12"
                  >
                    Back to Login
                  </Button>
                </div>
              </div>

              {/* Error Alert */}
              {error && (
                <Alert variant="destructive">
                  <AlertTriangle className="w-4 h-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Success Alert */}
              {success && (
                <Alert>
                  <CheckCircle className="w-4 h-4" />
                  <AlertDescription>{success}</AlertDescription>
                </Alert>
              )}

              {/* Worker-friendly troubleshooting */}
              <Alert>
                <Mail className="w-4 h-4" />
                <AlertDescription className="text-xs space-y-1">
                  <p><strong>💡 Quick tips:</strong></p>
                  <div className="space-y-1 mt-2">
                    <p>• Check your spam/junk folder (emails sometimes hide there)</p>
                    <p>• The email might take a few minutes to arrive</p>
                    <p>• You can continue using the app while waiting!</p>
                    <p>• We'll remind you gently to verify later</p>
                    {isDev && <p>• Dev mode: Check Supabase SMTP configuration</p>}
                  </div>
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}